// Client-side menu model for the Builder — mirrors the template's buildMenu
// (backend/src/render/templates/lagoon/index.ts). Produces the ordered list of
// menu sections (mandated groups always; optional sections only when enabled),
// honouring theme.menuOrder + theme.menuLabels.

import type { SiteBundle, SiteTheme } from './api.ts';

export interface MenuRow {
  id: string;
  labelDv: string;
  labelEn: string;
  mandated: boolean;
  enabled: boolean; // optional sections: their toggle; mandated: always true
}

export function buildMenuRows(bundle: SiteBundle): MenuRow[] {
  const theme = bundle.site.theme ?? {};
  const labels = theme.menuLabels ?? {};

  const mandated = bundle.groups.filter((g) => g.mandated);
  const enabledOptional = bundle.optional.filter((o) => o.enabled);

  const defaultOrder = [...mandated.map((g) => g.group), ...enabledOptional.map((o) => o.id)];
  const candidates = new Map<string, MenuRow>();
  for (const g of mandated) {
    candidates.set(g.group, {
      id: g.group,
      labelDv: labels[g.group]?.dv?.trim() || g.titleDv,
      labelEn: labels[g.group]?.en?.trim() || g.titleEn,
      mandated: true,
      enabled: true,
    });
  }
  for (const o of enabledOptional) {
    candidates.set(o.id, {
      id: o.id,
      labelDv: labels[o.id]?.dv?.trim() || o.titleDv,
      labelEn: labels[o.id]?.en?.trim() || o.titleEn,
      mandated: false,
      enabled: true,
    });
  }

  const saved = Array.isArray(theme.menuOrder) ? theme.menuOrder : [];
  const ordered: string[] = [];
  const seen = new Set<string>();
  for (const id of saved) if (candidates.has(id) && !seen.has(id)) { ordered.push(id); seen.add(id); }
  for (const id of defaultOrder) if (!seen.has(id)) { ordered.push(id); seen.add(id); }

  return ordered.map((id) => candidates.get(id)!);
}

// The full stored order (all mandated + all enabled-optional) after a move, so we
// can persist a stable menuOrder. `dir` is -1 (up) or +1 (down).
export function moveInOrder(rows: MenuRow[], id: string, dir: -1 | 1): string[] {
  const ids = rows.map((r) => r.id);
  const i = ids.indexOf(id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= ids.length) return ids;
  [ids[i], ids[j]] = [ids[j]!, ids[i]!];
  return ids;
}

// Merge a menu change into the existing theme (theme is replaced wholesale by the API).
export function withMenu(theme: SiteTheme, patch: Partial<Pick<SiteTheme, 'menuOrder' | 'menuLabels'>>): SiteTheme {
  return { ...theme, ...patch };
}
